import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Api } from "../../api/Assinafy"
import { Alert, Button, Form, Input } from "../../components"
import { Page, Section } from "../../layout"
import { isIdentityVerificationMethod } from "../../utils/identityVerification"
import SignerIdentityVerification from "./SignerIdentityVerification"
import { resolvePostVerificationStatus, resolveSigningLinkStatus } from "../Document/signingLinkStatusUtils"

const signerDocumentFromPayload = (data) => {
  if (!data) {
    return null
  }
  return Array.isArray(data) ? data[0] : data
}

const SignerVerification = () => {
  const { signerAccessCode } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [verificationCode, setVerificationCode] = useState([])
  const [formError, setFormError] = useState({})
  const [formLoading, setFormLoading] = useState(false)
  const [signer, setSigner] = useState(null)
  /** Evita piscar o formulário de código antes de saber o método do signatário. */
  const [signerLoaded, setSignerLoaded] = useState(false)

  const search = new URLSearchParams(location.search)
  const initialBanner =
    location.state?.alertMessage ||
    search.get("alertMessage") ||
    (search.get("channel") === "email" ? "Código de verificação enviada para o email" : "")
  const [bannerMessage, setBannerMessage] = useState(initialBanner)

  useEffect(() => {
    let active = true

    const loadSigner = () => Api("signer/self")(signerAccessCode)
      .then(({ status, data: { data } }) => {
        if (!active) return
        if (status === 200) {
          setSigner(data)

          if (data.verification_method === "Bypass") {
            handleBypassFlow(data)
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setSignerLoaded(true)
      })

    Api("signer/viewDocument")(signerAccessCode)
      .then(({ status, data: { data } }) => {
        if (!active) return

        const terminalStatus = status === 200
          ? resolveSigningLinkStatus(data, data.current_signer)
          : null

        if (terminalStatus) {
          navigate(`/sign/${signerAccessCode}`, {
            state: {
              verified: true,
              dataConfirmed: true,
              terminalDocument: data,
              terminalStatus,
              verification_method: data.current_signer?.verification_method,
            },
            replace: true,
          })
          return
        }

        loadSigner()
      })
      .catch(() => {
        if (active) loadSigner()
      })

    return () => {
      active = false
    }
  }, [signerAccessCode])

  /**
   * Leva o signatário verificado para a confirmação de dados do documento —
   * mesmo caminho do Bypass e do Certificado A1.
   */
  const navigateToDocument = useCallback(
    (signerData, verificationMethod) => {
      if (!signerData?.id) return

      Api("signer/document")(signerData.id, signerAccessCode)
        .then(({ status: docStatus, data: { data } }) => {
          const doc = signerDocumentFromPayload(data)
          const documentId = doc?.id || doc?.document_id
          if (docStatus !== 200 || !documentId) {
            setFormError({ type: "warning", message: "Documento não encontrado" })
            setFormLoading(false)
            return
          }

          navigate(`/sign/${signerAccessCode}/confirm-data/${documentId}`, {
            state: {
              verified: true,
              dataConfirmed: false,
              documentId,
              documentName: doc.name || signerData.document_name || "documento",
              ownerName: signerData.sender_name || "Proprietário do documento",
              verification_method: verificationMethod || signerData.verification_method,
            },
            replace: true,
          })
        })
        .catch(() => {
          setFormError({ type: "warning", message: "Erro ao buscar documento" })
          setFormLoading(false)
        })
    },
    [navigate, signerAccessCode],
  )

  const handleIdentityApproved = useCallback(() => {
    navigateToDocument(signer, signer?.verification_method)
  }, [signer, navigateToDocument])

  const handleBypassFlow = (signerData) => {

    if (signerData.id) {
      Api("signer/document")(signerData.id, signerAccessCode)
        .then(({ status: docStatus, data: { data } }) => {
          const doc = signerDocumentFromPayload(data)
          if (docStatus === 200 && doc && (doc.id || doc.document_id)) {
            const documentId = doc.id || doc.document_id
            const documentName = doc.name || signerData.document_name || "documento"

            if (documentId) {
              navigate(`/sign/${signerAccessCode}/confirm-data/${documentId}`, {
                state: {
                  verified: true,
                  dataConfirmed: false,
                  documentId: documentId,
                  documentName: documentName,
                  ownerName: signerData.sender_name || "Proprietário do documento",
                  verification_method: "Bypass",
                },
                replace: true,
              })
            }
          }
        })
        .catch((err) => {
          console.log(err?.response?.status)
        })
    }
  }

  const getNextStep = () => {
    if (!signer) {
      return `/sign/${signerAccessCode}`
    }

    const { verification_method, has_accepted_terms, is_new_signer } = signer

    if (!verification_method) {
      return `/sign/${signerAccessCode}`
    }

    if ((verification_method === "Email" || verification_method === "Bypass") && !has_accepted_terms) {
      return `/sign/${signerAccessCode}`
    }

    if (has_accepted_terms && signer.id) {
      return new Promise((resolve) => {
        Api("signer/document")(signer.id, signerAccessCode)
          .then(({ status: docStatus, data: { data } }) => {
            const doc = signerDocumentFromPayload(data)
            if (docStatus === 200 && doc && (doc.id || doc.document_id)) {
              const documentId = doc.id || doc.document_id
              resolve(`/sign/${signerAccessCode}/confirm-data/${documentId}`)
            } else {
              resolve(`/sign/${signerAccessCode}`)
            }
          })
          .catch(() => {
            resolve(`/sign/${signerAccessCode}`)
          })
      })
    }

    return `/sign/${signerAccessCode}`
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (verificationCode.length === 6) {
      setFormLoading(true)

      Api("signer/verifyCode")(signerAccessCode, verificationCode.join(""))
        .then((resp) => {
          if (resp.status === 200) {
            if (signer?.id) {
              Api("signer/document")(signer.id, signerAccessCode)
                .then(({ status: docStatus, data: { data } }) => {
                  const doc = signerDocumentFromPayload(data)
                  if (docStatus === 200 && doc && (doc.id || doc.document_id)) {
                    const documentId = doc.id || doc.document_id
                    const documentName = doc.name || signer?.document_name || "documento"

                    resolvePostVerificationStatus({
                      document: doc,
                      signer,
                      loadDocument: () =>
                        Api("signer/viewDocument")(signerAccessCode).then(
                          ({ data: { data: fullDocument } }) => fullDocument,
                        ),
                    }).then(({ document: statusDocument, status: terminalStatus }) => {
                      if (terminalStatus) {
                        navigate(`/sign/${signerAccessCode}`, {
                          state: {
                            verified: true,
                            dataConfirmed: true,
                            terminalDocument: statusDocument,
                            terminalStatus,
                            verification_method: "Email",
                          },
                          replace: true,
                        })
                        return
                      }

                      const nextStep = getNextStep()

                      Promise.resolve(nextStep).then((path) => {
                        navigate(path, {
                          state: {
                            verified: true,
                            documentId: documentId,
                            documentName: documentName,
                            dataConfirmed: false,
                            ownerName: signer?.sender_name || "Proprietário do documento",
                            verification_method: "Email",
                          },
                        })
                      })
                    })
                  } else {
                    setFormLoading(false)
                    setFormError({
                      type: "warning",
                      message: "Documento não encontrado",
                    })
                  }
                })
                .catch(() => {
                  setFormLoading(false)
                  setFormError({
                    type: "warning",
                    message: "Erro ao buscar documento",
                  })
                })
            } else {
              setFormLoading(false)
            }
          }
        })
        .catch(({ response }) => {
          if (response) {
            setFormError({
              type: "warning",
              message: response.data.message,
            })
          }
          setFormLoading(false)
        })
    }
  }

  useEffect(() => {
    const pasteAccessCode = (e) => {
      const accessCode = e.clipboardData.getData("Text")
      const accessCodeSplit = accessCode.replace(/[^\d]/g, "").split("")
      const inputs = document.querySelectorAll(".form-sign-verify input")
      const length = inputs.length
      let i = -1
      while (++i < length) {
        const idx = i
        const value = accessCodeSplit[i] || ""
        inputs[i].value = value
        setVerificationCode((prev) => {
          const code = [...prev]
          code[idx] = value
          return code
        })

        if (idx < 5 && value) {
          document.querySelector('[data-id="' + (idx + 1) + '"]').focus()
        } else if (idx > 0 && !value) {
          document.querySelector('[data-id="' + (idx - 1) + '"]').focus()
        }
      }
    }
    document.addEventListener("paste", pasteAccessCode)

    return () => {
      document.removeEventListener("paste", pasteAccessCode)
    }
  }, [signerAccessCode])

  if (!signerLoaded) {
    return (
      <Page className="page-sign">
        <Section>
          <Section.Header title="Carregando..." opening="Preparando a verificação do seu acesso" />
        </Section>
      </Page>
    )
  }

  if (isIdentityVerificationMethod(signer?.verification_method)) {
    return (
      <SignerIdentityVerification
        signerAccessCode={signerAccessCode}
        signer={signer}
        onApproved={handleIdentityApproved}
      />
    )
  }

  if (signer?.verification_method === "Bypass") {
    return (
      <Page className="page-sign">
        <Section>
          <Section.Header title="Processando..." opening="Redirecionando para confirmação de dados" />
        </Section>
      </Page>
    )
  }

  return (
    <Page className="page-sign">
      <Section>
        <Section.Header
          title="Olá, informe o código de verificação"
          opening="Insira o código de verificação enviado por e-mail para acessar e assinar o documento"
        />

        <Form id="form-signer-verification" className="form-sign form-sign-verify" onSubmit={handleSubmit}>
          {bannerMessage && (
            <Alert variant="success" onClose={() => setBannerMessage("")} style={{ marginBottom: 12 }}>
              {bannerMessage}
            </Alert>
          )}

          {formError.message && (
            <Alert variant={formError.type} onClose={() => setFormError({})}>
              {formError.message}
            </Alert>
          )}

          <Form.Group>
            {[0, 1, 2, 3, 4, 5].map((v) => (
              <Input.Tel
                key={v}
                autoFocus={v === 0}
                data-id={v}
                maxLength="1"
                required
                onChange={(e) => {
                  const idx = Number.parseInt(e.target.dataset.id, 10)
                  const value = e.target.value

                  setVerificationCode((prev) => {
                    const code = [...prev]
                    code[idx] = value
                    return code
                  })

                  if (idx < 5 && value) {
                    document.querySelector('[data-id="' + (idx + 1) + '"]').focus()
                  } else if (idx > 0 && !value) {
                    document.querySelector('[data-id="' + (idx - 1) + '"]').focus()
                  }
                }}
              />
            ))}
          </Form.Group>

          <div className="form-footer">
            <Button variant="primary" submit size="lg" loading={formLoading}>
              ACESSAR DOCUMENTO
            </Button>
          </div>
        </Form>
      </Section>
    </Page>
  )
}

export default SignerVerification
